import type {
    EntityCollection,
    EntityCollectionRaw,
    EntityPrefab,
    EntityPrefabType,
    EntityRawPrefab,
} from "@workadventure/map-editor";
import type { Readable, Writable } from "svelte/store";
import { derived, writable } from "svelte/store";
import { entitiesFileMigration } from "@workadventure/map-editor/src/Migrations/EntitiesFileMigration";
import { asError } from "catch-unknown";
import { EntityVariant } from "./Entities/EntityVariant";

const DYSTOPIA_PROP_COLLECTION_NAME = "Dystopia Rising";
const DYSTOPIA_PROP_ID_PREFIX = "dr:";
const DYSTOPIA_PROP_ID = /^dr:[a-z0-9]+(?:-[a-z0-9]+)*:[A-Z0-9]+(?:_[A-Z0-9]+)*$/;

type DystopiaPropPolicyAsset = Readonly<{
    manifestKey: string;
    displayName: string;
    presentationCategory: string;
    tags: readonly string[];
}>;

type DystopiaPropPolicyCategory = Readonly<{
    id: string;
    label: string;
    order: number;
}>;

type DystopiaPropPolicy = Readonly<{
    schemaVersion: "prop-placement-policy@1" | "prop-placement-policy@2";
    digest: string;
    categories: readonly DystopiaPropPolicyCategory[];
    assets: readonly DystopiaPropPolicyAsset[];
}>;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expectedKeys: readonly string[]): boolean {
    const actualKeys = Object.keys(value).sort();
    return actualKeys.length === expectedKeys.length && actualKeys.every((key, index) => key === expectedKeys[index]);
}

function humanizeDystopiaPropCategoryId(categoryId: string): string {
    return categoryId
        .replace(/^dr-/, "")
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function parseDystopiaPropPolicy(value: unknown): DystopiaPropPolicy | undefined {
    if (!isRecord(value)) {
        return undefined;
    }
    const schemaVersion = value.schemaVersion;
    if (
        (schemaVersion !== "prop-placement-policy@1" && schemaVersion !== "prop-placement-policy@2") ||
        !hasExactKeys(
            value,
            schemaVersion === "prop-placement-policy@2"
                ? ["assets", "categories", "digest", "schemaVersion"]
                : ["assets", "digest", "schemaVersion"],
        ) ||
        typeof value.digest !== "string" ||
        !/^[0-9a-f]{64}$/.test(value.digest) ||
        !Array.isArray(value.assets) ||
        value.assets.length > 5_000
    ) {
        return undefined;
    }

    const categories: DystopiaPropPolicyCategory[] = [];
    const categoryIds = new Set<string>();
    const categoryOrders = new Set<number>();
    if (schemaVersion === "prop-placement-policy@2") {
        if (!Array.isArray(value.categories) || value.categories.length === 0 || value.categories.length > 256) {
            return undefined;
        }
        let previousOrder = 0;
        for (const rawCategory of value.categories) {
            if (
                !isRecord(rawCategory) ||
                !hasExactKeys(rawCategory, ["id", "label", "order"]) ||
                typeof rawCategory.id !== "string" ||
                rawCategory.id.length > 64 ||
                !/^dr-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(rawCategory.id) ||
                typeof rawCategory.label !== "string" ||
                rawCategory.label.trim() !== rawCategory.label ||
                rawCategory.label.length === 0 ||
                rawCategory.label.length > 100 ||
                !Number.isSafeInteger(rawCategory.order) ||
                Number(rawCategory.order) <= 0 ||
                Number(rawCategory.order) <= previousOrder ||
                categoryIds.has(rawCategory.id) ||
                categoryOrders.has(Number(rawCategory.order))
            ) {
                return undefined;
            }
            const category = {
                id: rawCategory.id,
                label: rawCategory.label,
                order: Number(rawCategory.order),
            };
            categories.push(category);
            categoryIds.add(category.id);
            categoryOrders.add(category.order);
            previousOrder = category.order;
        }
    }

    const assets: DystopiaPropPolicyAsset[] = [];
    const manifestKeys = new Set<string>();
    for (const rawAsset of value.assets) {
        if (
            !isRecord(rawAsset) ||
            !hasExactKeys(rawAsset, ["displayName", "manifestKey", "presentationCategory", "tags"]) ||
            typeof rawAsset.manifestKey !== "string" ||
            !DYSTOPIA_PROP_ID.test(rawAsset.manifestKey) ||
            rawAsset.manifestKey.length > 200 ||
            typeof rawAsset.displayName !== "string" ||
            rawAsset.displayName.trim() === "" ||
            rawAsset.displayName.length > 200 ||
            typeof rawAsset.presentationCategory !== "string" ||
            rawAsset.presentationCategory.length > 64 ||
            !/^dr-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(rawAsset.presentationCategory) ||
            !Array.isArray(rawAsset.tags) ||
            rawAsset.tags.length > 32 ||
            !rawAsset.tags.every(
                (tag) => typeof tag === "string" && tag.trim() !== "" && tag.length <= 64,
            ) ||
            new Set(rawAsset.tags).size !== rawAsset.tags.length ||
            (schemaVersion === "prop-placement-policy@2" &&
                !categoryIds.has(rawAsset.presentationCategory)) ||
            manifestKeys.has(rawAsset.manifestKey)
        ) {
            return undefined;
        }
        manifestKeys.add(rawAsset.manifestKey);
        assets.push({
            manifestKey: rawAsset.manifestKey,
            displayName: rawAsset.displayName,
            presentationCategory: rawAsset.presentationCategory,
            tags: rawAsset.tags,
        });
    }

    if (schemaVersion === "prop-placement-policy@1") {
        for (const [index, id] of [...new Set(assets.map((asset) => asset.presentationCategory))]
            .sort()
            .entries()) {
            categories.push({ id, label: humanizeDystopiaPropCategoryId(id), order: index + 1 });
        }
    }

    return {
        schemaVersion,
        digest: value.digest,
        categories,
        assets,
    };
}

export class EntitiesCollectionsManager {
    private entitiesPrefabsMapPromise!: Promise<Map<string, EntityPrefab>>;
    private tags: string[] = [];

    private currentCollection: EntityCollection = { collectionName: "All Object Collection", collection: [], tags: [] };
    private readonly entitiesPrefabsStore: Writable<EntityPrefab[]>;
    private readonly entitiesPrefabsVariantStore: Readable<EntityVariant[]>;

    constructor() {
        this.entitiesPrefabsStore = writable([]);
        this.entitiesPrefabsVariantStore = derived(this.entitiesPrefabsStore, ($entitiesPrefabsStore) => {
            // entityVariants ordered by collectionName+name
            const entityVariants = new Map<string, EntityVariant>();
            for (const entityPrefab of $entitiesPrefabsStore) {
                const idOrGeneratedName =
                    entityPrefab.type === "Custom"
                        ? entityPrefab.id
                        : entityPrefab.collectionName + "__" + entityPrefab.name;
                let variant = entityVariants.get(idOrGeneratedName);
                if (variant === undefined) {
                    variant = new EntityVariant(entityPrefab);
                    entityVariants.set(idOrGeneratedName, variant);
                } else {
                    variant.addPrefab(entityPrefab);
                }
            }
            return [...entityVariants.values()];
        });
    }

    private dystopiaPropPolicy: ReadonlyMap<string, DystopiaPropPolicyAsset> | undefined;
    private dystopiaPropCategories: ReadonlyMap<string, DystopiaPropPolicyCategory> = new Map();
    private dystopiaPropPolicyRequestId = 0;

    private updateEntitiesPrefabsStore(): void {
        this.entitiesPrefabsStore.set(
            this.currentCollection.collection.flatMap((prefab) => {
                const hasDystopiaCollection = prefab.collectionName === DYSTOPIA_PROP_COLLECTION_NAME;
                const hasDystopiaId = prefab.id.startsWith(DYSTOPIA_PROP_ID_PREFIX);
                if (!hasDystopiaCollection && !hasDystopiaId) {
                    // Non-DR prefabs are hidden entirely: WorkAdventure's stock collections
    // (Furniture, Office) load as
                    // type "Default" and clash with the wasteland art style, so they
                    // are hidden from the picker for everyone, staff included. Only
                    // user-uploaded "Custom" prefabs pass through un-governed. The
                    // full-collection prefab map is untouched, so already-placed
                    // stock entities keep rendering.
                    return [];
                }

                // A mismatched collection/id pair is never a valid governed prop.
                // Hide it even when a manifest entry happens to share the id.
                if (!hasDystopiaCollection || !hasDystopiaId) {
                    return [];
                }

                const policyAsset = this.dystopiaPropPolicy?.get(prefab.id);
                if (policyAsset === undefined) {
                    return [];
                }
                return [
                    {
                        ...prefab,
                        name: policyAsset.displayName,
                        // The first tag is the sole browse category. Remaining
                        // discovery tags stay available to the existing search.
                        tags: [
                            policyAsset.presentationCategory,
                            ...policyAsset.tags.filter((tag) => tag !== policyAsset.presentationCategory),
                        ],
                    },
                ];
            }),
        );
    }

    public async refreshDystopiaPropPolicy(userRoomToken: string | undefined): Promise<void> {
        const requestId = ++this.dystopiaPropPolicyRequestId;
        this.dystopiaPropPolicy = undefined;
        this.dystopiaPropCategories = new Map();
        this.updateEntitiesPrefabsStore();
        if (userRoomToken === undefined || userRoomToken === "") {
            return;
        }

        try {
            const requestPolicy = (path: string) => fetch(path, {
                method: "GET",
                headers: { "x-dr-room-token": userRoomToken },
                credentials: "omit",
                cache: "no-store",
                redirect: "error",
            });
            let response = await requestPolicy("/editor-assets/prop-policy.v2.json");
            if (response.status === 404) {
                // A rolling deployment may still have the exact v1-only API. Its
                // authority projection remains safe; labels/order degrade locally.
                response = await requestPolicy("/editor-assets/prop-policy.json");
            }
            if (!response.ok) {
                throw new Error("Policy request was rejected");
            }
            const policy = parseDystopiaPropPolicy(await response.json());
            if (policy === undefined) {
                throw new Error("Policy response did not match a supported prop-placement-policy schema");
            }

            await this.entitiesPrefabsMapPromise;
            if (requestId !== this.dystopiaPropPolicyRequestId) {
                return;
            }
            this.dystopiaPropPolicy = new Map(policy.assets.map((asset) => [asset.manifestKey, asset]));
            this.dystopiaPropCategories = new Map(policy.categories.map((category) => [category.id, category]));
            this.updateEntitiesPrefabsStore();
        } catch {
            if (requestId === this.dystopiaPropPolicyRequestId) {
                this.dystopiaPropPolicy = undefined;
                this.dystopiaPropCategories = new Map();
                this.updateEntitiesPrefabsStore();
            }
            // Never include the credential-bearing request or token in diagnostics.
            console.error("DR prop policy refresh failed; governed props remain hidden.");
        }
    }

    public getDystopiaPropCategory(categoryId: string): DystopiaPropPolicyCategory | undefined {
        return this.dystopiaPropCategories.get(categoryId);
    }

    public getEntitiesPrefabsVariantStore(): Readable<EntityVariant[]> {
        return this.entitiesPrefabsVariantStore;
    }

    public async getEntityPrefab(collectionName: string, entityPrefabId: string): Promise<EntityPrefab | undefined> {
        const prefabsMap = await this.entitiesPrefabsMapPromise;
        return prefabsMap.get(entityPrefabId);
    }

    public loadCollections(collectionDescriptors: { url: string; type: EntityPrefabType }[]): void {
        this.entitiesPrefabsMapPromise = new Promise<Map<string, EntityPrefab>>((resolve, reject) => {
            const entityCollections: { collection: EntityCollection; url: string }[] = [];
            const fetchUrlPromises: Promise<EntityCollectionRaw>[] = [];
            for (const descriptor of collectionDescriptors) {
                const promise = this.fetchRawCollection(descriptor.url);
                fetchUrlPromises.push(promise);
            }

            //TODO check tagSet and this.currentCollection
            Promise.allSettled(fetchUrlPromises)
                .then((promises) => {
                    for (let i = 0; i < promises.length; i++) {
                        const promise = promises[i];
                        const descriptor = collectionDescriptors[i];
                        if (promise.status === "fulfilled") {
                            let entityCollectionRaw = promise.value;
                            try {
                                entityCollectionRaw = entitiesFileMigration.migrate(entityCollectionRaw);
                                entityCollections.push({
                                    collection: this.parseRawCollection(entityCollectionRaw, descriptor.type),
                                    url: descriptor.url,
                                });
                            } catch (error) {
                                console.error("Error while parsing entity collection", descriptor.url, error);
                            }
                        } else {
                            console.error("Error while loading entity collection", descriptor.url, promise.reason);
                        }
                    }

                    for (const { collection, url } of entityCollections) {
                        const tagSet = new Set<string>();
                        collection.collection.forEach((entity: EntityPrefab) => {
                            this.currentCollection.collection.push({
                                name: entity.name,
                                id: entity.id,
                                collectionName: entity.collectionName,
                                depthOffset: entity.depthOffset ?? 0,
                                tags: [...entity.tags, ...collection.tags],
                                imagePath: new URL(entity.imagePath, url).toString(),
                                direction: entity.direction,
                                color: entity.color,
                                collisionGrid: entity.collisionGrid,
                                type: entity.type,
                            });
                            entity.tags.forEach((tag: string) => tagSet.add(tag));
                        });

                        const tags = [...new Set([...tagSet, ...this.tags])];
                        tags.sort();
                        this.tags = tags;
                    }
                    // Keep the complete collection for prefab resolution and
                    // project only policy-approved DR props into the picker.
                    this.updateEntitiesPrefabsStore();

                    const prefabsMap = new Map<string, EntityPrefab>();
                    for (const prefab of this.currentCollection.collection) {
                        prefabsMap.set(prefab.id, prefab);
                    }
                    resolve(prefabsMap);
                })
                .catch((error) => {
                    console.error(error);
                    reject(error instanceof Error ? error : new Error(JSON.stringify(error)));
                });
        });
    }

    public addUploadedEntity(uploadedEntity: EntityRawPrefab, customEntityCollectionUrl: string) {
        const uploadedEntityPrefab: EntityPrefab = {
            ...this.parseRawEntityPrefab("custom entities", uploadedEntity, "Custom"),
            imagePath: new URL(uploadedEntity.imagePath, customEntityCollectionUrl).toString(),
        };
        this.currentCollection.collection.push(uploadedEntityPrefab);

        this.entitiesPrefabsStore.update((currentEntitiesPrefabs) => [...currentEntitiesPrefabs, uploadedEntityPrefab]);
        this.entitiesPrefabsMapPromise = new Promise<Map<string, EntityPrefab>>((resolve, reject) => {
            this.entitiesPrefabsMapPromise
                .then((existingEntitiesPrefabsMap) => {
                    existingEntitiesPrefabsMap.set(uploadedEntityPrefab.id, uploadedEntityPrefab);
                    resolve(existingEntitiesPrefabsMap);
                })
                .catch((error) => {
                    console.error(error);
                    reject(asError(error));
                });
        });
    }

    public modifyCustomEntity(
        id: string,
        name: string,
        tags: string[],
        depthOffset?: number,
        collisionGrid?: number[][],
    ): void {
        this.entitiesPrefabsStore.update((currentEntitiesPrefabs) => {
            const indexOfCustomEntity = currentEntitiesPrefabs.findIndex((entityPrefab) => entityPrefab.id === id);
            if (indexOfCustomEntity !== -1) {
                currentEntitiesPrefabs[indexOfCustomEntity] = {
                    ...currentEntitiesPrefabs[indexOfCustomEntity],
                    name,
                    tags,
                    depthOffset,
                    collisionGrid,
                };
            }
            return currentEntitiesPrefabs;
        });
        this.entitiesPrefabsMapPromise = new Promise<Map<string, EntityPrefab>>((resolve, reject) => {
            this.entitiesPrefabsMapPromise
                .then((existingEntitiesPrefabsMap) => {
                    const entity = existingEntitiesPrefabsMap.get(id);
                    if (entity) {
                        existingEntitiesPrefabsMap.set(id, {
                            ...entity,
                            name,
                            tags,
                            depthOffset,
                            collisionGrid,
                        });
                    }
                    resolve(existingEntitiesPrefabsMap);
                })
                .catch((error) => {
                    console.error(error);
                    reject(asError(error));
                });
        });
    }

    public deleteCustomEntity(id: string): void {
        this.entitiesPrefabsStore.update((currentEntitiesPrefabs) => {
            return currentEntitiesPrefabs.filter((entityPrefab) => entityPrefab.id !== id);
        });
        this.entitiesPrefabsMapPromise = new Promise<Map<string, EntityPrefab>>((resolve, reject) => {
            this.entitiesPrefabsMapPromise
                .then((existingEntitiesPrefabsMap) => {
                    existingEntitiesPrefabsMap.delete(id);
                    resolve(existingEntitiesPrefabsMap);
                })
                .catch((error) => {
                    console.error(error);
                    reject(asError(error));
                });
        });
    }

    private async fetchRawCollection(url: string): Promise<EntityCollectionRaw> {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
        }
        return response.json();
    }

    private parseRawCollection(
        rawCollection: EntityCollectionRaw,
        rawCollectionType: EntityPrefabType,
    ): EntityCollection {
        return {
            collectionName: rawCollection.collectionName,
            tags: [...rawCollection.tags],
            collection: rawCollection.collection.map((rawPrefab: EntityRawPrefab) =>
                this.parseRawEntityPrefab(rawCollection.collectionName, rawPrefab, rawCollectionType),
            ),
        };
    }

    private parseRawEntityPrefab(
        collectionName: string,
        rawPrefab: EntityRawPrefab,
        entityPrefabType: EntityPrefabType,
    ): EntityPrefab {
        return {
            ...rawPrefab,
            collectionName,
            type: entityPrefabType,
        };
    }
}
